import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import api, { asArray } from "@/lib/api";

const TABS = [
  { key: "all", label: "All" },
  { key: "resin-art", label: "Resin" },
  { key: "handmade-bags", label: "Bags" },
  { key: "pearl-jewellery", label: "Pearl" },
];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get(`/gallery${tab === "all" ? "" : `?category=${tab}`}`).then((r) => setItems(asArray(r.data))).catch(() => {});
  }, [tab]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="gallery-page">
      <div className="border-b divider-gold pb-10">
        <div className="overline text-emerald mb-3">Studio journal</div>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight">Gallery</h1>
        <p className="mt-4 max-w-lg text-charcoal/60">Small moments from the studio floor. What we poured this week, what we wrapped in linen this morning.</p>
      </div>

      <div className="flex gap-6 mt-8 mb-12 text-sm">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`gallery-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`nav-link overline ${tab === t.key ? "text-emerald" : "text-charcoal/60"}`}
            data-active={tab === t.key}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]">
        {items.map((g, i) => (
          <motion.button
            key={g.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: (i % 8) * 0.04 }}
            onClick={() => setLightbox(g)}
            data-testid={`gallery-item-${i}`}
            className="mb-4 break-inside-avoid w-full img-hover block"
          >
            <img src={g.url} alt={g.caption} loading="lazy" className="w-full object-cover" />
            {g.caption && <div className="mt-2 text-xs text-charcoal/60 italic">{g.caption}</div>}
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-charcoal/90 z-[100] flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
          >
            <button data-testid="close-lightbox" className="absolute top-6 right-6 text-ivory p-2" onClick={() => setLightbox(null)}><X className="w-5 h-5" /></button>
            <motion.img
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              src={lightbox.url}
              alt={lightbox.caption}
              className="max-h-[85vh] max-w-[92vw] object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
